import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation as useRoute } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import Nav from "@/components/Nav";
import PawBazaarFooter, { ComingSoon } from "@/components/PawBazaarFooter";
import { PhoneTabBar } from "@/components/PawBazaarSites";
import Analytics from "@/components/Analytics";
import OrderTracking from "@/pages/OrderTracking";
import { ContactPage, HelpCentre, PolicyPage, SellerSupport } from "@/pages/InfoPages";
import { GuidePage, LearnHome } from "@/pages/Learn";
import ProtectedRoute from "@/components/ProtectedRoute";
import ReauthDialog from "@/components/ReauthDialog";
import Landing from "@/pages/Landing";
import { SignInPage, SignUpPage, ConfirmPage, ForgotPage } from "@/components/CustomerAuth";
import VendorRegister from "@/pages/VendorRegister";
import Browse from "@/pages/Browse";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Donate from "@/pages/Donate";
import VendorPublic from "@/pages/VendorPublic";
import Account from "@/pages/Account";

// Customer marketplace app. The Operations Console (admin/vendor/support/
// delivery) is a separate React app mounted at /console.
function App() {
  return (
    <div className="App min-h-screen">
      <AuthProvider>
        <LocationProvider>
          <CartProvider>
            <BrowserRouter>
              <WishlistProvider>
              <Toaster position="top-right" richColors />
          <Analytics />
              <ReauthDialog />
              <Nav />
              <main className="min-h-[80vh]">
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<SignInPage />} />
                  <Route path="/register" element={<SignUpPage />} />
                  <Route path="/confirm" element={<ConfirmPage />} />
                  <Route path="/forgot-password" element={<ForgotPage />} />
                  <Route path="/reset-password" element={<ForgotPage />} />
                  <Route path="/vendor/register" element={<VendorRegister />} />
                  <Route path="/browse" element={<Browse />} />
                  <Route path="/shop" element={<Browse />} />
                  <Route path="/vendors" element={<Browse />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/vendor/:id" element={<VendorPublic />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/donate/:needId" element={<Donate />} />

                  {/* My account: one page with tabs; the old pages open their tab. */}
                  <Route path="/account" element={<ProtectedRoute roles={["customer"]}><Account /></ProtectedRoute>} />
                  <Route path="/account/devices" element={<Navigate to="/account?tab=profile&section=devices" replace />} />
                  <Route path="/profile" element={<OldAccountPage tab="profile" />} />
                  <Route path="/customer" element={<OldAccountPage view="shop" />} />
                  <Route path="/orders/:id/track" element={<ProtectedRoute roles={["customer"]}><OrderTracking /></ProtectedRoute>} />
                  <Route path="/pets" element={<OldAccountPage tab="pets" />} />
                  <Route path="/wishlist" element={<OldAccountPage tab="wishlist" />} />
                  <Route path="/help" element={<OldAccountPage tab="help" />} />
                  <Route path="/help-centre" element={<HelpCentre />} />
                  <Route path="/learn" element={<LearnHome />} />
                  <Route path="/learn/:id" element={<GuidePage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/policies/:slug" element={<PolicyPage />} />
                  <Route path="/sellers" element={<SellerSupport />} />
                  <Route path="/soon/:slug" element={<ComingSoon />} />
                  <Route path="*" element={
                    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="not-found">
                      <h1 className="font-heading text-4xl font-semibold mb-3">Page not found</h1>
                      <p className="text-stone-600 mb-6">The page you're looking for doesn't exist on the PawBazaar store.</p>
                      <a href="/" className="inline-flex items-center px-5 py-2.5 rounded-full bg-stoneWarm text-white text-sm font-semibold hover:bg-stoneWarm/90">Back to home</a>
                    </div>
                  } />
                </Routes>
              </main>
              <PawBazaarFooter current="shop" />
              <PhoneMenu />
              </WishlistProvider>
            </BrowserRouter>
          </CartProvider>
        </LocationProvider>
      </AuthProvider>
    </div>
  );
}

export default App;

/** The shop's menu at the bottom on phones and tablets. */
function PhoneMenu() {
  const { pathname, search } = useRoute();
  const cart = useCart();
  return <PhoneTabBar current="shop" path={pathname} search={search} cartCount={cart.count} />;
}

/** An old account address (/profile, /customer, /pets, /wishlist, /help):
 *  opens its tab of My account, keeping a sub-tab like ?tab=devices. */
function OldAccountPage({ tab, view }) {
  const { search } = useRoute();
  const old = new URLSearchParams(search);
  const next = new URLSearchParams();
  if (tab) next.set("tab", tab);
  if (view) next.set("view", old.get("tab") === "subs" ? "subs" : view);
  if (tab === "profile" && old.get("tab")) next.set("section", old.get("tab"));
  return <Navigate to={`/account?${next.toString()}`} replace />;
}
